import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, XCircle, Eye, Download } from 'lucide-react';
import Header from './components/Header20';
import Footer from './components/Footer20';
import Button from './components/Button';

const Orders = () => {
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState('all');

    // Sample orders data
    const orders = [
        {
            id: 'ORD12345',
            date: '2025-01-15',
            status: 'delivered',
            items: [
                { name: 'Abstract Expression Tee', quantity: 2, price: 1299, image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=100&h=120&fit=crop' },
                { name: 'Urban Nights Hoodie', quantity: 1, price: 2499, image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=100&h=120&fit=crop' },
            ],
            total: 5097,
            trackingNumber: 'TRK987654321',
        },
        {
            id: 'ORD12346',
            date: '2025-01-18',
            status: 'shipped',
            items: [
                { name: 'Nature Print Sweatshirt', quantity: 1, price: 1899, image: 'https://images.unsplash.com/photo-1578587018452-892bacefd3f2?w=100&h=120&fit=crop' },
            ],
            total: 1899,
            trackingNumber: 'TRK987654322',
            estimatedDelivery: '2025-01-22',
        },
        {
            id: 'ORD12347',
            date: '2025-01-20',
            status: 'processing',
            items: [
                { name: 'Geometric Pattern Jacket', quantity: 1, price: 3499, image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=100&h=120&fit=crop' },
                { name: 'City Lights Tee', quantity: 2, price: 1199, image: 'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=100&h=120&fit=crop' },
            ],
            total: 5897,
        },
        {
            id: 'ORD12348',
            date: '2025-01-10',
            status: 'cancelled',
            items: [
                { name: 'Forest Dreams Hoodie', quantity: 1, price: 2299, image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=100&h=120&fit=crop' },
            ],
            total: 2299,
        },
    ];

    const statusConfig = {
        processing: {
            icon: Clock,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100',
            label: 'Processing',
        },
        shipped: {
            icon: Truck,
            color: 'text-orange-600',
            bgColor: 'bg-orange-100',
            label: 'Shipped',
        },
        delivered: {
            icon: CheckCircle,
            color: 'text-green-600',
            bgColor: 'bg-green-100',
            label: 'Delivered',
        },
        cancelled: {
            icon: XCircle,
            color: 'text-red-600',
            bgColor: 'bg-red-100',
            label: 'Cancelled',
        },
    };

    const filters = [
        { id: 'all', label: 'All Orders', count: orders.length },
        { id: 'processing', label: 'Processing', count: orders.filter(o => o.status === 'processing').length },
        { id: 'shipped', label: 'Shipped', count: orders.filter(o => o.status === 'shipped').length },
        { id: 'delivered', label: 'Delivered', count: orders.filter(o => o.status === 'delivered').length },
    ];

    const filteredOrders = activeFilter === 'all'
        ? orders
        : orders.filter(order => order.status === activeFilter);

    if (orders.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex flex-col">
                <Header cartCount={0} />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md px-4">
                        <Package size={80} className="text-orange-300 mx-auto mb-6" />
                        <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                            No Orders Yet
                        </h2>
                        <p className="text-gray-600 mb-8">
                            You haven't placed any orders yet. Start shopping to see your orders here!
                        </p>
                        <Button variant="primary" size="lg" onClick={() => navigate('/template20/shop')}>
                            Start Shopping
                        </Button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
            <Header showBackButton={true} cartCount={3} />

            <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
                <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-8">
                    My Orders
                </h1>

                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-8">
                    {filters.map(filter => (
                        <button
                            key={filter.id}
                            onClick={() => setActiveFilter(filter.id)}
                            className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                                activeFilter === filter.id
                                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg scale-105'
                                    : 'bg-white text-gray-700 hover:bg-orange-50 border border-orange-200'
                            }`}
                        >
                            {filter.label}
                            <span className="ml-2 text-sm opacity-75">({filter.count})</span>
                        </button>
                    ))}
                </div>

                {/* Orders List */}
                <div className="space-y-6">
                    {filteredOrders.map(order => {
                        const StatusIcon = statusConfig[order.status].icon;

                        return (
                            <div key={order.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                                {/* Order Header */}
                                <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-orange-100">
                                    <div className="flex flex-wrap justify-between items-center gap-4">
                                        <div className="flex flex-wrap items-center gap-4">
                                            <div>
                                                <p className="text-sm text-gray-600">Order ID</p>
                                                <p className="font-semibold text-gray-900">{order.id}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">Order Date</p>
                                                <p className="font-semibold text-gray-900">
                                                    {new Date(order.date).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">Total</p>
                                                <p className="font-semibold text-gray-900">₹{order.total}</p>
                                            </div>
                                        </div>
                                        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${statusConfig[order.status].bgColor}`}>
                                            <StatusIcon size={18} className={statusConfig[order.status].color} />
                                            <span className={`font-semibold ${statusConfig[order.status].color}`}>
                                                {statusConfig[order.status].label}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Order Items */}
                                <div className="p-6">
                                    <div className="space-y-4 mb-6">
                                        {order.items.map((item, index) => (
                                            <div key={index} className="flex gap-4">
                                                <div className="w-20 h-24 flex-shrink-0 overflow-hidden rounded-lg">
                                                    <img
                                                        src={item.image}
                                                        alt={item.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="font-semibold text-gray-900 mb-1">{item.name}</h4>
                                                    <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                                                    <p className="text-sm font-semibold text-gray-900 mt-1">
                                                        ₹{item.price} each
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Tracking Info */}
                                    {order.trackingNumber && (
                                        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 mb-4">
                                            <div className="flex items-center justify-between flex-wrap gap-3">
                                                <div>
                                                    <p className="text-sm text-gray-600 mb-1">Tracking Number</p>
                                                    <p className="font-semibold text-gray-900">{order.trackingNumber}</p>
                                                    {order.estimatedDelivery && (
                                                        <p className="text-sm text-gray-600 mt-2">
                                                            Est. Delivery: {new Date(order.estimatedDelivery).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric'
                                                            })}
                                                        </p>
                                                    )}
                                                </div>
                                                {order.status === 'shipped' && (
                                                    <Button variant="secondary" size="sm">
                                                        <Truck size={16} className="inline mr-2" />
                                                        Track Order
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex flex-wrap gap-3">
                                        <Button variant="outline" size="sm" onClick={() => navigate(`/template20/order/${order.id}`)}>
                                            <Eye size={16} className="inline mr-2" />
                                            View Details
                                        </Button>
                                        {order.status === 'delivered' && (
                                            <>
                                                <Button variant="secondary" size="sm">
                                                    <Download size={16} className="inline mr-2" />
                                                    Download Invoice
                                                </Button>
                                                <Button variant="primary" size="sm">
                                                    Reorder
                                                </Button>
                                            </>
                                        )}
                                        {order.status === 'processing' && (
                                            <Button variant="outline" size="sm">
                                                Cancel Order
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {filteredOrders.length === 0 && (
                    <div className="text-center py-16">
                        <Package size={64} className="text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg mb-4">No orders found in this category</p>
                        <Button variant="outline" onClick={() => setActiveFilter('all')}>
                            View All Orders
                        </Button>
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default Orders;
